import { useMemo } from 'react';
import {
  Card,
  Col,
  Row,
  Statistic,
  Typography,
  Spin,
  Alert,
  Tag,
} from 'antd';
import {
  WarningOutlined,
  ClockCircleOutlined,
  CheckCircleOutlined,
  SafetyCertificateOutlined,
  FieldTimeOutlined,
} from '@ant-design/icons';
import { useServiceDashboard } from '@hooks/useAnalytics';

const mdPriorityColors: Record<string, string> = {
  Critical: '#ff4d4f',
  High: '#fa8c16',
  Normal: '#1677ff',
  Low: '#8c8c8c',
};

const mdPriorityLabels: Record<string, string> = {
  Critical: 'Критичний',
  High: 'Високий',
  Normal: 'Нормальний',
  Low: 'Низький',
};

const mdStatusColors: Record<string, string> = {
  New: 'blue',
  InProgress: 'processing',
  WaitingParts: 'orange',
  OnHold: 'default',
  Completed: 'success',
  Cancelled: 'error',
};

const mdStatusLabels: Record<string, string> = {
  New: 'Нова',
  InProgress: 'В роботі',
  WaitingParts: 'Очікування запчастин',
  OnHold: 'На паузі',
  Completed: 'Завершена',
  Cancelled: 'Скасована',
};

export default function ServiceDashboardPage() {
  const { data: mdData, isLoading: mdIsLoading } = useServiceDashboard();

  const mdMaxPriorityCount = useMemo(() => {
    if (!mdData?.byPriority || mdData.byPriority.length === 0) return 1;
    return Math.max(...mdData.byPriority.map((mdItem) => mdItem.count), 1);
  }, [mdData]);

  const mdMaxTrendValue = useMemo(() => {
    if (!mdData?.serviceTrend || mdData.serviceTrend.length === 0) return 1;
    return Math.max(
      ...mdData.serviceTrend.map((mdItem) => Math.max(mdItem.created, mdItem.closed)),
      1
    );
  }, [mdData]);

  const mdMonthNames = [
    'Січ', 'Лют', 'Бер', 'Кві', 'Тра', 'Чер',
    'Лип', 'Сер', 'Вер', 'Жов', 'Лис', 'Гру',
  ];

  if (mdIsLoading) {
    return (
      <div style={{ textAlign: 'center', padding: 48 }}>
        <Spin size="large" />
      </div>
    );
  }

  if (!mdData) {
    return <Alert type="warning" message="Дані сервісу недоступні" showIcon />;
  }

  return (
    <>
      <Typography.Title level={4} style={{ marginBottom: 16 }}>
        Дашборд сервісу
      </Typography.Title>

      {/* Summary cards */}
      <Row gutter={16} style={{ marginBottom: 24 }}>
        <Col span={5}>
          <Card>
            <Statistic
              title="Відкритих"
              value={mdData.totalOpenRequests}
              prefix={<ClockCircleOutlined />}
            />
          </Card>
        </Col>
        <Col span={5}>
          <Card>
            <Statistic
              title="SLA порушено"
              value={mdData.slaBreachedCount}
              prefix={<WarningOutlined />}
              valueStyle={{ color: mdData.slaBreachedCount > 0 ? '#cf1322' : '#3f8600' }}
            />
          </Card>
        </Col>
        <Col span={5}>
          <Card>
            <Statistic
              title="Закритих за місяць"
              value={mdData.completedThisMonth}
              prefix={<CheckCircleOutlined />}
              valueStyle={{ color: '#3f8600' }}
            />
          </Card>
        </Col>
        <Col span={5}>
          <Card>
            <Statistic
              title="Гарантія закінчується"
              value={mdData.warrantyExpiringCount}
              prefix={<SafetyCertificateOutlined />}
              valueStyle={{ color: mdData.warrantyExpiringCount > 0 ? '#faad14' : undefined }}
            />
          </Card>
        </Col>
        <Col span={4}>
          <Card>
            <Statistic
              title="Середній час (год)"
              value={mdData.avgCloseTimeHours}
              precision={1}
              prefix={<FieldTimeOutlined />}
            />
          </Card>
        </Col>
      </Row>

      <Row gutter={16} style={{ marginBottom: 24 }}>
        {/* By priority */}
        <Col span={12}>
          <Card title="За пріоритетом" size="small">
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              {mdData.byPriority.map((mdItem) => {
                const mdColor = mdPriorityColors[mdItem.priority] ?? '#8c8c8c';
                const mdLabel = mdPriorityLabels[mdItem.priority] ?? mdItem.priority;
                const mdBarWidth = (mdItem.count / mdMaxPriorityCount) * 100;
                return (
                  <div key={mdItem.priority}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                      <Typography.Text>{mdLabel}</Typography.Text>
                      <Typography.Text strong>{mdItem.count}</Typography.Text>
                    </div>
                    <div style={{ height: 20, backgroundColor: '#f5f5f5', borderRadius: 4, overflow: 'hidden' }}>
                      <div
                        style={{
                          width: `${mdBarWidth}%`,
                          height: '100%',
                          backgroundColor: mdColor,
                          borderRadius: 4,
                          transition: 'width 0.3s ease',
                          minWidth: mdItem.count > 0 ? 4 : 0,
                        }}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          </Card>
        </Col>

        {/* By status */}
        <Col span={12}>
          <Card title="За статусом" size="small">
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12 }}>
              {mdData.byStatus.map((mdItem) => {
                const mdColor = mdStatusColors[mdItem.status] ?? 'default';
                const mdLabel = mdStatusLabels[mdItem.status] ?? mdItem.status;
                return (
                  <div
                    key={mdItem.status}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 8,
                      padding: '8px 16px',
                      borderRadius: 8,
                      backgroundColor: '#fafafa',
                      border: '1px solid #f0f0f0',
                    }}
                  >
                    <Tag color={mdColor} style={{ margin: 0 }}>
                      {mdLabel}
                    </Tag>
                    <Typography.Text strong style={{ fontSize: 18 }}>
                      {mdItem.count}
                    </Typography.Text>
                  </div>
                );
              })}
            </div>
          </Card>
        </Col>
      </Row>

      {/* Monthly trend */}
      <Card title="Тренд сервісних заявок (12 місяців)" size="small">
        <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end', height: 200, padding: '0 8px' }}>
          {mdData.serviceTrend.map((mdItem) => {
            const mdCreatedHeight = (mdItem.created / mdMaxTrendValue) * 160;
            const mdClosedHeight = (mdItem.closed / mdMaxTrendValue) * 160;
            const mdMonthLabel = mdMonthNames[mdItem.month - 1] ?? String(mdItem.month);
            return (
              <div
                key={`${mdItem.year}-${mdItem.month}`}
                style={{
                  flex: 1,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: 2,
                }}
              >
                <div style={{ display: 'flex', gap: 2, alignItems: 'flex-end', height: 170 }}>
                  <div
                    title={`Створено: ${mdItem.created}`}
                    style={{
                      width: 16,
                      height: Math.max(mdCreatedHeight, 2),
                      backgroundColor: '#fa8c16',
                      borderRadius: '2px 2px 0 0',
                    }}
                  />
                  <div
                    title={`Закрито: ${mdItem.closed}`}
                    style={{
                      width: 16,
                      height: Math.max(mdClosedHeight, 2),
                      backgroundColor: '#52c41a',
                      borderRadius: '2px 2px 0 0',
                    }}
                  />
                </div>
                <Typography.Text
                  type="secondary"
                  style={{ fontSize: 11, lineHeight: '16px' }}
                >
                  {mdMonthLabel}
                </Typography.Text>
              </div>
            );
          })}
        </div>
        <div style={{ display: 'flex', gap: 24, justifyContent: 'center', marginTop: 8 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            <div style={{ width: 12, height: 12, borderRadius: 2, backgroundColor: '#fa8c16' }} />
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>Створено</Typography.Text>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            <div style={{ width: 12, height: 12, borderRadius: 2, backgroundColor: '#52c41a' }} />
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>Закрито</Typography.Text>
          </div>
        </div>
      </Card>
    </>
  );
}
